import moment from "moment";
import { useMemo } from "react";
import currency from "currency.js";
import { useTranslation } from "react-i18next";
import {
  GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner,
  SideResponse,
} from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { COMPONENTS } from "../../constants/design/spacing";
import { getL2ExplorerUrl, getUsdcDecimals } from "../../config/chains";
import { nanosToSeconds } from "../../utils/date";
import { formatSizePrecision } from "../../utils/format";
import { rawToDecimal, getMarketDecimals } from "../../utils/rawConversion";
import { UNKNOWN_DISPLAY } from "../../utils/unknownNumbers";
import { Spinner } from "../shared/Spinner";
import { SpinnerContainerWrapper } from "../shared/Spinner/style";
import {
  RowWithSizeIndicator,
  TableHeaderWrapper,
} from "../shared/Table/style";
import {
  RowUpdater,
  TradeOrderBookWrapper,
} from "../TicketDetails/TradeOrderBook/style";
import { TradeColumn } from "./style";

interface IHeader {
  title: string;
  flex: number;
}

const emptyRow: Partial<GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner> =
  {
    amount: undefined,
    price: undefined,
    side: undefined,
    created_timestamp: undefined,
  };

const defaultHistoryRows: Partial<GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner>[] =
  [emptyRow, emptyRow, emptyRow, emptyRow];

/**
 * Time for a trade row: `HH:mm:ss` for today, with the date otherwise. Quiet
 * markets list fills weeks old; as bare times they read as today's (Arc
 * ETH-USDC's panel reached back 99 days, 2026-09-11).
 */
export const formatTradeTime = (unixSeconds: number, now: moment.Moment = moment()) => {
  const at = moment.unix(unixSeconds);
  return at.isSame(now, "day") ? at.format("HH:mm:ss") : at.format("MMM D HH:mm");
};

const looksRawInteger = (val: string | undefined): boolean => {
  if (!val || val.includes(".")) return false;
  return /^\d{10,}$/.test(val);
};

interface ISpreadProps {
  textColor: string;
  timestamp: number | null;
  price: string | null;
  size: string | null;
  sizeDecimals: number;
  priceDecimals: number;
  rowHeight?: string;
  headers: Array<IHeader>;
  txHash?: string;
}

function TradeRow({
  price,
  size,
  timestamp,
  textColor,
  sizeDecimals,
  priceDecimals,
  rowHeight,
  headers,
  txHash,
}: ISpreadProps) {
  const explorerUrl = getL2ExplorerUrl();
  const baseUrl = explorerUrl?.endsWith("/") ? explorerUrl : `${explorerUrl}/`;
  const txLink = txHash && baseUrl ? `${baseUrl}tx/${txHash}` : undefined;

  const content = (
    <RowWithSizeIndicator
      showBorderBottom
      highlightOnHover
      style={{
        height: rowHeight || COMPONENTS.tableRowSmall,
        cursor: txLink ? "pointer" : undefined,
      }}
    >
      <TradeColumn
        flex={headers[0].flex}
        $color={size ? textColor : TEXT_COLORS.one}
      >
        {price ? currency(price, { precision: priceDecimals }).format() : "-"}
      </TradeColumn>
      <TradeColumn flex={headers[1].flex}>
        {size ? formatSizePrecision(size, sizeDecimals) : "-"}
      </TradeColumn>
      <TradeColumn
        $color={TEXT_COLORS.two}
        flex={headers[2].flex}
        $singleLine
        title={
          timestamp
            ? moment.unix(timestamp).format("YYYY-MM-DD HH:mm:ss")
            : undefined
        }
      >
        {timestamp ? formatTradeTime(timestamp) : "-"}
      </TradeColumn>
    </RowWithSizeIndicator>
  );

  if (txLink) {
    return (
      <a
        href={txLink}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: "none", color: "inherit", display: "contents" }}
      >
        {content}
      </a>
    );
  }

  return content;
}

interface IInstrumentTradeHistoryProps {
  sizeDecimals: number;
  priceDecimals: number;
  trades?: GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner[];
  loading?: boolean;
  rowHeight?: string;
}

function InstrumentTradeHistory({
  trades,
  loading,
  rowHeight,
  sizeDecimals,
  priceDecimals,
}: IInstrumentTradeHistoryProps) {
  const { t } = useTranslation("app", { keyPrefix: "InstrumentTradeHistory" });
  const headers = useMemo(
    () => [
      { title: t("price"), flex: 4 },
      { title: t("size"), flex: 3 },
      // 3, not 2: "MMM D HH:mm" needs ~72px, which flex 2 of the 304px panel lacked.
      { title: t("time"), flex: 3 },
    ],
    [t]
  );

  // Convert raw values using market-specific decimals
  const PRICE_DECIMALS = getUsdcDecimals();

  const cvPrice = (val: string | undefined) => {
    if (!val) return "";
    if (!looksRawInteger(val)) return val;
    return rawToDecimal(val, PRICE_DECIMALS);
  };

  const cvAmount = (val: string | undefined, instrumentName?: string) => {
    if (!val) return "";
    if (!looksRawInteger(val)) return val;
    // Market-specific base decimals (e.g. 8 for WBTC, 18 for WNATIVE). The
    // fallback used to be WETH's, which silently mis-sized every other token.
    const decimals = instrumentName ? getMarketDecimals(instrumentName) : undefined;
    if (!decimals) return UNKNOWN_DISPLAY;
    return rawToDecimal(val, decimals.baseDecimals);
  };

  return (
    <TradeOrderBookWrapper>
      {!trades && loading ? (
        <SpinnerContainerWrapper $height={100}>
          <Spinner />
        </SpinnerContainerWrapper>
      ) : (
        <>
          <TableHeaderWrapper>
            <RowWithSizeIndicator>
              {headers.map((header) => (
                <TradeColumn key={header.title} flex={header.flex}>
                  {header.title}
                </TradeColumn>
              ))}
            </RowWithSizeIndicator>
          </TableHeaderWrapper>
          <div>
            {(trades?.length ? trades : defaultHistoryRows).map(
              ({ amount, price, side, created_timestamp, trade_id, ...rest }, index) => (
                <RowUpdater
                  // eslint-disable-next-line react/no-array-index-key
                  key={trade_id || `${index}`}
                >
                  <TradeRow
                    rowHeight={rowHeight}
                    priceDecimals={priceDecimals}
                    sizeDecimals={sizeDecimals}
                    price={cvPrice(price)}
                    size={cvAmount(amount, (rest as any).instrument_name)}
                    txHash={(rest as any).tx_hash}
                    timestamp={nanosToSeconds(created_timestamp || 0)}
                    textColor={
                      side === SideResponse.Buy
                        ? COLORS.up.one
                        : COLORS.down.one
                    }
                    headers={headers}
                  />
                </RowUpdater>
              )
            )}
          </div>
        </>
      )}
    </TradeOrderBookWrapper>
  );
}

export default InstrumentTradeHistory;
